import {
  getIncompleteMonthInfo,
  isExtrapolatableSourceField,
  sliceClubDisplayWindow,
  sortRowsChronologically,
} from '../../utils/clubGrowthMetrics';
import {
  ComparisonStatus,
  formatKpiMonthOptionLabel,
  METRICS,
  MetricConfig,
  PulseRow,
} from './marketPulseConstants';
import { formatPulseMonthLabel, getStatus, kpiPeriodGrowth, rowKey } from './marketPulseFormatting';
import { mergePartialMarketRows } from './marketPulseComparisonData';

export interface BranchCoverageIssue {
  id: string;
  monthLabel: string;
  orderDays: number;
  fullDays: number;
}

export type KpiDataNoticeRole = 'period-start' | 'period-end' | 'excluded-from-kpi';

export interface KpiDataNoticeItem extends BranchCoverageIssue {
  role: KpiDataNoticeRole;
}

export interface MarketPulseKpiItem {
  metric: MetricConfig;
  branchGrowth: number | null;
  marketGrowth: number | null;
  lead: number | null;
  status: ComparisonStatus;
  usesExtrapolation: boolean;
}

export interface MarketPulseKpiData {
  kpis: MarketPulseKpiItem[];
  kpiPeriodLabel: string | null;
  kpiEndMonthLabel: string | null;
  kpiComparisonNoticeItems: KpiDataNoticeItem[];
  kpiExcludedNoticeItems: KpiDataNoticeItem[];
  hasKpiDataNotice: boolean;
  extrapolate: boolean;
  hasExtrapolatedMonths: boolean;
  kpiUsesExtrapolation: boolean;
  /** Keys of the resolved start/end rows (for period control UI). */
  resolvedStartKey: string | null;
  resolvedEndKey: string | null;
  /** Chronological month options inside the display window. */
  monthOptions: KpiMonthOption[];
}

export interface KpiMonthOption {
  value: string;
  label: string;
}

export interface KpiPeriodOverride {
  startKey: string;
  endKey: string;
}

export interface KpiPeriodKeys {
  startKey: string | null;
  endKey: string | null;
}

const buildBranchCoverageIssue = (row: PulseRow, sourceData: PulseRow[]): BranchCoverageIssue | null => {
  const info = getIncompleteMonthInfo(row, sourceData);
  if (!info.incomplete) return null;
  return {
    id: rowKey(row),
    monthLabel: formatPulseMonthLabel(row),
    orderDays: info.orderDays,
    fullDays: info.fullDays,
  };
};

const findRowByKey = (rows: PulseRow[], key: string): PulseRow | null =>
  rows.find((row) => rowKey(row) === key) ?? null;

/** Default KPI start/end keys for a chronologically sorted display window. */
export function getDefaultKpiPeriod(branchWindow: PulseRow[], extrapolate: boolean): KpiPeriodKeys {
  const startRow = branchWindow.length >= 1 ? branchWindow[0] : null;
  const endRow = extrapolate
    ? (branchWindow.length ? branchWindow[branchWindow.length - 1] : null)
    : (branchWindow.length >= 2 ? branchWindow[branchWindow.length - 2] : null);

  return {
    startKey: startRow ? rowKey(startRow) : null,
    endKey: endRow ? rowKey(endRow) : null,
  };
}

/** Month options for the KPI period selectors (chronological order). */
export function listKpiMonthOptions(
  branchWindow: PulseRow[],
  sourceData: PulseRow[] = branchWindow
): KpiMonthOption[] {
  return branchWindow.map((row) => {
    const info = getIncompleteMonthInfo(row, sourceData);
    return {
      value: rowKey(row),
      label: formatKpiMonthOptionLabel(
        formatPulseMonthLabel(row),
        info.orderDays,
        info.fullDays,
        info.incomplete
      ),
    };
  });
}

/**
 * Ensure start is strictly before end in the window.
 * If invalid, adjust the opposite side to the nearest valid neighbor.
 */
export function sanitizeKpiPeriodKeys(
  branchWindow: PulseRow[],
  startKey: string,
  endKey: string,
  preferAdjust: 'start' | 'end' = 'end'
): KpiPeriodKeys {
  if (branchWindow.length < 2) {
    const only = branchWindow[0] ? rowKey(branchWindow[0]) : null;
    return { startKey: only, endKey: only };
  }

  const indexByKey = new Map(branchWindow.map((row, i) => [rowKey(row), i]));
  let startIdx = indexByKey.get(startKey);
  let endIdx = indexByKey.get(endKey);

  if (startIdx === undefined) startIdx = 0;
  if (endIdx === undefined) endIdx = branchWindow.length - 1;

  if (startIdx >= endIdx) {
    if (preferAdjust === 'end') {
      endIdx = Math.min(startIdx + 1, branchWindow.length - 1);
      if (endIdx <= startIdx) {
        startIdx = Math.max(0, endIdx - 1);
      }
    } else {
      startIdx = Math.max(0, endIdx - 1);
      if (startIdx >= endIdx) {
        endIdx = Math.min(startIdx + 1, branchWindow.length - 1);
      }
    }
  }

  return {
    startKey: rowKey(branchWindow[startIdx]),
    endKey: rowKey(branchWindow[endIdx]),
  };
}

export function computeMarketPulseKpiData(
  branchAll: PulseRow[],
  marketAll: PulseRow[],
  baseMonths: number,
  extrapolate: boolean,
  periodOverride?: KpiPeriodOverride | null,
  marketPartialLatest: PulseRow[] = [],
): MarketPulseKpiData {
  const effectiveMarketAll = extrapolate
    ? mergePartialMarketRows(marketAll, marketPartialLatest)
    : marketAll;
  const branchWindow = sliceClubDisplayWindow(sortRowsChronologically(branchAll), baseMonths);
  const marketWindow = sliceClubDisplayWindow(sortRowsChronologically(effectiveMarketAll), baseMonths);
  const monthOptions = listKpiMonthOptions(branchWindow, branchAll);
  const defaults = getDefaultKpiPeriod(branchWindow, extrapolate);

  let kpiStartRow: PulseRow | null = null;
  let kpiEndRow: PulseRow | null = null;

  if (periodOverride?.startKey && periodOverride?.endKey && branchWindow.length >= 2) {
    const sanitized = sanitizeKpiPeriodKeys(
      branchWindow,
      periodOverride.startKey,
      periodOverride.endKey,
      'end'
    );
    kpiStartRow = sanitized.startKey ? findRowByKey(branchWindow, sanitized.startKey) : null;
    kpiEndRow = sanitized.endKey ? findRowByKey(branchWindow, sanitized.endKey) : null;
  } else {
    kpiStartRow = defaults.startKey ? findRowByKey(branchWindow, defaults.startKey) : null;
    kpiEndRow = defaults.endKey ? findRowByKey(branchWindow, defaults.endKey) : null;
  }

  const marketSorted = sortRowsChronologically(effectiveMarketAll);
  const marketKpiStartRow = kpiStartRow ? findRowByKey(marketWindow, rowKey(kpiStartRow)) : null;
  const marketKpiEndRow = kpiEndRow ? findRowByKey(marketWindow, rowKey(kpiEndRow)) : null;

  // Fall back to full market series if window slice missed the key
  const marketStartResolved =
    marketKpiStartRow ?? (kpiStartRow ? findRowByKey(marketSorted, rowKey(kpiStartRow)) : null);
  let marketEndResolved =
    marketKpiEndRow ?? (kpiEndRow ? findRowByKey(marketSorted, rowKey(kpiEndRow)) : null);

  // Extrapolate mode defaults end to the incomplete current month; market MoM
  // (OrderDays > 20) often omits it. Fall back to latest month in both series.
  if (extrapolate && kpiEndRow && !marketEndResolved && branchWindow.length >= 2) {
    const marketKeySet = new Set(marketSorted.map((row) => rowKey(row)));
    const startKey = kpiStartRow ? rowKey(kpiStartRow) : null;
    const startIdx = startKey
      ? branchWindow.findIndex((row) => rowKey(row) === startKey)
      : -1;

    for (let i = branchWindow.length - 1; i >= 0; i--) {
      if (startIdx >= 0 && i <= startIdx) break;
      const candidate = branchWindow[i];
      const key = rowKey(candidate);
      if (!marketKeySet.has(key)) continue;
      const marketCandidate = findRowByKey(marketSorted, key);
      if (!marketCandidate) continue;
      kpiEndRow = candidate;
      marketEndResolved = marketCandidate;
      break;
    }
  }

  const resolvedStartKey = kpiStartRow ? rowKey(kpiStartRow) : null;
  const resolvedEndKey = kpiEndRow ? rowKey(kpiEndRow) : null;

  const kpiPeriodLabel = kpiEndRow && kpiStartRow
    ? `رشد ${formatPulseMonthLabel(kpiEndRow)} نسبت به ${formatPulseMonthLabel(kpiStartRow)}`
    : null;
  const kpiEndMonthLabel = kpiEndRow ? formatPulseMonthLabel(kpiEndRow) : null;

  const latestRow = branchWindow.length ? branchWindow[branchWindow.length - 1] : null;
  const latestMonthInfo = latestRow ? getIncompleteMonthInfo(latestRow, branchAll) : null;
  const hasIncompleteLatestMonth = latestMonthInfo?.incomplete ?? false;

  const hasExtrapolatedMonths = branchWindow.some((row) => extrapolate && getIncompleteMonthInfo(row, branchAll).incomplete);

  const kpiEndIncomplete = kpiEndRow ? getIncompleteMonthInfo(kpiEndRow, branchAll).incomplete : false;
  const kpiStartIncomplete = kpiStartRow ? getIncompleteMonthInfo(kpiStartRow, branchAll).incomplete : false;
  const kpiUsesExtrapolation = extrapolate && (kpiEndIncomplete || kpiStartIncomplete);

  const kpis = METRICS.map((m) => {
    const branchGrowth = kpiPeriodGrowth(kpiEndRow, kpiStartRow, m.sourceField, branchAll, extrapolate);
    const marketGrowth = kpiPeriodGrowth(marketEndResolved, marketStartResolved, m.sourceField, effectiveMarketAll, extrapolate);
    const lead = branchGrowth !== null && marketGrowth !== null ? branchGrowth - marketGrowth : null;
    const usesExtrapolation = kpiUsesExtrapolation && isExtrapolatableSourceField(m.sourceField);
    return { metric: m, branchGrowth, marketGrowth, lead, status: getStatus(lead), usesExtrapolation };
  });

  const kpiComparisonNoticeItems: KpiDataNoticeItem[] = [];
  const kpiExcludedNoticeItems: KpiDataNoticeItem[] = [];
  const kpiNoticeSeen = new Set<string>();

  const pushKpiNotice = (row: PulseRow | null, role: KpiDataNoticeRole) => {
    if (!row) return;
    const issue = buildBranchCoverageIssue(row, branchAll);
    if (!issue || kpiNoticeSeen.has(issue.id)) return;
    kpiNoticeSeen.add(issue.id);
    const item = { ...issue, role };
    if (role === 'excluded-from-kpi') {
      kpiExcludedNoticeItems.push(item);
    } else {
      kpiComparisonNoticeItems.push(item);
    }
  };

  if (kpiStartRow && getIncompleteMonthInfo(kpiStartRow, branchAll).incomplete) {
    pushKpiNotice(kpiStartRow, 'period-start');
  }
  if (
    kpiEndRow &&
    kpiEndRow !== kpiStartRow &&
    getIncompleteMonthInfo(kpiEndRow, branchAll).incomplete
  ) {
    pushKpiNotice(kpiEndRow, 'period-end');
  }
  if (!extrapolate && hasIncompleteLatestMonth && latestRow) {
    const latestKey = rowKey(latestRow);
    const usedInKpi =
      (kpiStartRow && rowKey(kpiStartRow) === latestKey) ||
      (kpiEndRow && rowKey(kpiEndRow) === latestKey);
    if (!usedInKpi) {
      pushKpiNotice(latestRow, 'excluded-from-kpi');
    }
  }

  const hasKpiDataNotice =
    kpiComparisonNoticeItems.length > 0 || kpiExcludedNoticeItems.length > 0;

  return {
    kpis,
    kpiPeriodLabel,
    kpiEndMonthLabel,
    kpiComparisonNoticeItems,
    kpiExcludedNoticeItems,
    hasKpiDataNotice,
    extrapolate,
    hasExtrapolatedMonths,
    kpiUsesExtrapolation,
    resolvedStartKey,
    resolvedEndKey,
    monthOptions,
  };
}
